import axios from 'axios';
import { BASE_SERVER_URL } from './sampleData';
import {
  getAccessToken,
  getRefreshToken,
  getActiveBusinessId,
  saveTokens,
  clearSession,
} from './auth/tokenStore';
import { installNetActivity } from './netActivity';

// Counts in-flight requests. First, so its response handler runs before the
// refresh-and-retry one below (see netActivity.js).
installNetActivity(axios);

// Critical for cross-domain cookie sending — the server also sets the
// refresh token as an httpOnly cookie on /auth.
axios.defaults.withCredentials = true;

const isProduction = process.env.NODE_ENV === 'production';

// Endpoints that must never trigger the refresh-and-retry dance: refreshing
// on a failed login (or on a failed refresh) would loop forever.
const AUTH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout'];
const isAuthPath = (url) => AUTH_PATHS.some((p) => String(url || '').includes(p));

// Background reads that several shared components fire on mount regardless of
// role. A driver has no settings:read, so these 403 on pages they are fully
// entitled to use — the page degrades quietly instead of nagging them.
// A refused business switch is explained by the switcher itself.
const QUIET_403_PATHS = ['/settings/getAll', '/auth/switch-business'];
const isQuiet403 = (url) => QUIET_403_PATHS.some((p) => String(url || '').includes(p));

axios.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    // This is critical for cross-domain requests - reinforces the global setting
    config.withCredentials = true;

    config.headers = { ...config.headers, Accept: 'application/json' };

    // Let axios/the browser set the multipart boundary itself for uploads;
    // forcing application/json here makes axios serialise the FormData away.
    const isFormData =
      typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (!isFormData && !config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    // Present only while the user is inside a business (opened from the group
    // overview, cleared on exit) — requests from the overview itself carry none.
    // A caller that names a business explicitly (the group users page writes
    // to a business the user is not inside) keeps its own header.
    const businessId = getActiveBusinessId();
    if (businessId && !config.headers['business-id']) {
      config.headers['business-id'] = businessId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* Single-flight refresh: several requests can 401 at once (the access token
   lives 15 minutes and everything expires together), but the refresh token is
   single-use — firing two refreshes in parallel burns the second one and the
   server revokes the whole session. All callers await the same promise. */
let refreshing = null;

/* The access token a failed request actually carried, so we can tell "my
   token expired" apart from "my token was replaced while I was in flight". */
function tokenSentWith(config) {
  const headers = config.headers || {};
  const raw =
    (typeof headers.get === 'function' && headers.get('Authorization')) ||
    headers.Authorization ||
    headers.authorization;
  return typeof raw === 'string' ? raw.replace(/^Bearer /, '') : null;
}

function refreshSession() {
  refreshing ??= axios
    .post(
      `${BASE_SERVER_URL}/auth/refresh`,
      { refresh_token: getRefreshToken() },
      { withCredentials: true }
    )
    .then((res) => {
      const data = (res && res.data && res.data.data) || {};
      if (!data.access_token) throw new Error('No access token in refresh response');
      saveTokens(data.access_token, data.refresh_token);
      // Role/business can change between refreshes — let the app re-read them.
      try {
        window.dispatchEvent(
          new CustomEvent('rf:refreshed', { detail: { business: data.business } })
        );
      } catch (_) {}
      return data;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

function endSession() {
  clearSession();
  try {
    window.dispatchEvent(new Event('rf:unauthorized'));
  } catch (_) {}
  try {
    if (window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
  } catch (_) {}
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!isProduction) {
      console.error('API Error:', error);
    }

    const status = error?.response?.status;
    const original = error?.config;

    // 401 = token problem. Refresh once, then replay the original request.
    // With no refresh token there is nothing to refresh — a signed-out tab
    // must not fire a doomed /auth/refresh on every call.
    if (
      status === 401 &&
      original &&
      !original._retried &&
      !isAuthPath(original.url) &&
      getRefreshToken()
    ) {
      // A request already in flight when someone else's refresh landed comes
      // back 401 holding the superseded token. Refreshing again would burn a
      // second single-use refresh token; replaying with the current one is
      // all it needs.
      const sentWith = tokenSentWith(original);
      if (!sentWith || sentWith === getAccessToken()) {
        try {
          await refreshSession();
        } catch (_) {
          endSession();
          return Promise.reject(error);
        }
      }
      original._retried = true;
      original.headers = {
        ...original.headers,
        Authorization: `Bearer ${getAccessToken()}`,
      };
      return axios(original);
    }

    // A 401 we could not recover from (refresh itself failed, or the retry
    // 401'd again) ends the session. Legacy 419/440 keep their old meaning.
    if (status === 401 || status === 419 || status === 440) {
      endSession();
      return Promise.reject(error);
    }

    // 403 = permission problem. The user is signed in and stays signed in —
    // unless their membership was revoked mid-session.
    if (status === 403) {
      const message = error?.response?.data?.message || '';
      if (message.includes('no longer a member')) {
        endSession();
      } else if (!isQuiet403(original && original.url)) {
        try {
          window.dispatchEvent(
            new CustomEvent('rf:forbidden', { detail: { message } })
          );
        } catch (_) {}
      }
    }

    return Promise.reject(error);
  }
);

export default axios;
